<template>
    <div
        :class="[
            'scope-input',
            {
                '-have-icon': icon,
                '-full-width': fullWidth,
                '-clear-button':
                    haveClearButton || haveCopyButton || haveUnitMeasure,
                '-only-search': haveClearButton || (haveCopyButton && !icon),
                '-has-save': hasSave
            }
        ]"
    >
        <div class="scope-input__label-wrapper" v-if="label">
            <div class="scope-input__label">
                {{ normalizedLabel }}
                <span v-if="showRequiredMark" class="scope-input__required">
                    *
                </span>
            </div>
        </div>
        <div class="scope-input__prompt" v-if="prompt">{{ prompt }}</div>
        <div :class="['scope-input__container', `-${size}`]">
            <div v-if="sideSign" class="scope-input__sign">{{ sideSign }}</div>

            <div v-if="$slots.start" class="scope-input__start-slot">
                <slot name="start" />
            </div>

            <input
                ref="inputRef"
                :disabled="disabled"
                class="scope-input__input"
                :class="[
                    `-${size}`,
                    {
                        '-invalid': invalid,
                        '-side-sign': Boolean(sideSign),
                        '-has-save': hasSave
                    }
                ]"
                v-model="vModel"
                :style="styles"
                :placeholder="placeholder"
                :veeBind="veeBind"
                autocomplete="off"
                @input="emit('input')"
                @keypress="handleKeyPress"
                @keyup.enter="emit('enter')"
                @keyup.esc="emit('esc')"
                :maxlength="isTimeInput ? 8 : maxLength"
                :pattern="pattern"
                :type="computedType"
            />
        </div>
        <div
            :class="[
                'scope-input__icon-wrapper',
                `-${size}`,
                {
                    '-only-search':
                        haveClearButton || hasSave || (haveCopyButton && !icon),
                    '-with-label': !!label,
                    '-have-unit-type': haveUnitMeasure,
                    '-password-type': type === 'password'
                }
            ]"
        >
            <IconNew v-if="icon" :icon="icon" class="scope-input__icon" />
            <IconNew
                v-if="haveClearButton"
                class="scope-input__clear"
                icon="Cross"
                @click="vModel = ''"
            />
            <IconNew
                v-if="haveCopyButton && !isValueCopy"
                class="scope-input__clear"
                icon="Copy"
                @click="copyHandler(vModel)"
            />
            <IconNew
                v-if="haveCopyButton && isValueCopy"
                class="scope-input__clear"
                icon="Check"
            />
            <template v-if="type === 'password'">
                <IconNew
                    v-if="!showPassword"
                    class="scope-input__eye"
                    icon="EyeClosedOutline"
                    @click="togglePasswordVisibility"
                />
                <IconNew
                    v-else
                    class="scope-input__eye"
                    icon="EyeOpenedOutline"
                    @click="togglePasswordVisibility"
                />
            </template>

            <div
                class="scope-input__save"
                v-if="hasSave && vModel && vModel.length"
                @click="emit('save')"
            >
                {{ saveBtnCta }}
            </div>
            <div
                class="scope-input__text"
                v-if="haveUnitMeasure && unitMeasure"
            >
                {{ unitMeasure }}
            </div>
        </div>
        <div class="scope-input__error" v-if="invalid && errorMessage">
            {{ errorMessage }}
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { useToastNew, IconNew } from '../index';

const { error } = useToastNew();

const emit = defineEmits(['input', 'enter', 'esc', 'save', 'clickOutside']);

const inputRef = ref(null);
const vModel = defineModel();

const props = defineProps({
    size: {
        type: String,
        default: 'md'
    },
    maxLength: {
        type: Number,
        default: 524288
    },
    onlyNumbers: Boolean,
    onlyNumberAndDot: Boolean,
    onlyNumberDotMinus: Boolean,
    isTimeInput: Boolean,
    icon: String,
    placeholder: String,
    haveClearButton: Boolean,
    haveCopyButton: Boolean,
    haveUnitMeasure: Boolean,
    unitMeasure: String,
    veeBind: Object,
    invalid: Boolean,
    fullWidth: Boolean,
    errorMessage: String,
    label: String,
    disabled: Boolean,
    width: String,
    sideSign: String,
    type: {
        default: 'text',
        type: String
    },
    pattern: String,
    hasSave: Boolean,
    saveBtnCta: {
        default: 'Save',
        type: String
    },
    prompt: String,
    required: { type: Boolean, default: false }
});

const styles = computed(() => (props.width ? { width: props.width } : null));

const handleKeyPress = (event) => {
    if (!props.onlyNumbers && !props.onlyNumberAndDot && !props.onlyNumberDotMinus) return;
    let regex;
    if (props.onlyNumbers) regex = /[0-9]/;
    else if (props.onlyNumberAndDot) regex = /[0-9.]/;
    else if (props.onlyNumberDotMinus) regex = /[0-9.-]/;
    if (!regex.test(event.key)) event.preventDefault();
};
const showRequiredMark = computed(() =>
    Boolean(props.required || /\*\s*$/.test(props.label || ''))
);
const normalizedLabel = computed(() =>
    (props.label || '').replace(/\s*\*$/, '')
);

const showPassword = ref(false);
const computedType = computed(() => {
    if (props.type === 'password') {
        return showPassword.value ? 'text' : 'password';
    }
    return props.type;
});

const togglePasswordVisibility = () => {
    showPassword.value = !showPassword.value;
};

const isValueCopy = ref(false);

const focus = () => {
    inputRef.value.focus();
};

const copyHandler = (value) => {
    navigator.clipboard
        .writeText(value)
        .then(() => {
            isValueCopy.value = true;
            const timeout = setTimeout(() => {
                isValueCopy.value = false;
                clearTimeout(timeout);
            }, 3000);
        })
        .catch((err) => {
            error(err);
        });
};

defineExpose({ focus });

const lastValidTime = ref('');

const handleTimeInput = (event) => {
    let input = event.target;
    let value = input.value;
    let digits = value.replace(/\D/g, '');

    // Валідація
    if (digits.length >= 2 && parseInt(digits.substring(0, 2), 10) > 23) {
        digits = digits.substring(0, 1);
    }
    if (digits.length >= 4 && parseInt(digits.substring(2, 4), 10) > 59) {
        digits = digits.substring(0, 3);
    }
    if (digits.length >= 6 && parseInt(digits.substring(4, 6), 10) > 59) {
        digits = digits.substring(0, 5);
    }

    let formatted = '';
    if (digits.length > 0) formatted = digits.substring(0, 2);
    if (digits.length > 2) formatted += ':' + digits.substring(2, 4);
    if (digits.length > 4) formatted += ':' + digits.substring(4, 6);

    vModel.value = formatted;

    nextTick(() => {
        input.value = formatted;
    });
};

const handleTimeBlur = () => {
    let value = vModel.value;
    if (!value) {
        vModel.value = lastValidTime.value;
        return;
    }

    let [h, m, s] = value.split(':');

    h = (h || '').padStart(2, '0');
    m = (m || '').padStart(2, '0');
    s = (s || '').padStart(2, '0');

    const finalTime = `${h}:${m}:${s}`;
    vModel.value = finalTime;
    lastValidTime.value = finalTime;
};

const handleTimeKeyDown = (event) => {
    if (event.key === 'Backspace' && event.target.value.endsWith(':')) {
        event.preventDefault();
        vModel.value = event.target.value.slice(0, -2); // ЗМІНЕНО: оновлюємо vModel
    }
};

onMounted(() => {
    if (props.isTimeInput) {
        if (vModel.value) {
            lastValidTime.value = vModel.value;
        }
        inputRef.value.addEventListener('input', handleTimeInput);
        inputRef.value.addEventListener('blur', handleTimeBlur);
        inputRef.value.addEventListener('keydown', handleTimeKeyDown);
    }
});
</script>

<style lang="scss" scoped>
@import '../assets/styles/_main.scss';

.scope-input {
    display: flex;
    flex-direction: column;
    gap: 8px;

    position: relative;
    width: fit-content;

    &__save,
    &__clear,
    &__icon,
    &__text,
    &__eye {
        z-index: 20;
    }

    &__start-slot {
        display: flex;
        align-items: center;
        padding-left: 12px;
    }

    &__icon {
        pointer-events: none;
    }

    &__clear {
        color: $text-mid-emphasis;
        cursor: pointer;
    }

    &__save {
        color: $primary-accent;
        @include fz-14;
        font-weight: 500;
        cursor: pointer;
    }

    &__text {
        @include fz-14;
        font-weight: 500;
        color: $text-mid-emphasis;
    }

    &__eye {
        cursor: pointer;
        color: $text-mid-emphasis;
    }

    &.-full-width {
        width: 100%;
    }

    &__container {
        display: flex;
        border: 1px solid $border;
        background: $card-background;
        border-radius: 10px;
        width: 100%;

        &.-lg {
            height: 40px;
        }

        &.-md {
            height: 36px;
        }

        &.-sm {
            height: 32px;
            border-radius: 8px;
        }

        &:has(input:hover:not(:disabled)),
        .scope-input:has(.scope-input__icon-wrapper:hover) &:not(:has(input:disabled)) {
            border-color: $primary-accent;
        }

        &:has(input:active:not(:disabled)),
        &:has(input:focus:hover),
        &:has(input:focus) {
            border-color: $primary-accent;
        }

        &:has(input.-invalid) {
            border-color: $border-error;
        }
    }

    &__sign {
        display: flex;
        height: 100%;
        align-items: center;
        justify-content: center;
        width: 40px;
        border-radius: 10px 0px 0px 10px;
        color: $text-mid-emphasis;
        background: $card-neutral-background;
    }

    input {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        position: relative;
        z-index: 10;

        text-overflow: ellipsis;
        border: unset;
        border-radius: 10px;
        outline: none;
        padding: 4px 12px;

        font-family: 'Geist', sans-serif;
        font-optical-sizing: auto;
        @include fz-14;
        font-weight: 500;
        color: $text-high-emphasis;
        background: $card-background;

        &.-sm {
            border-radius: 8px;
            padding: 4px 8px;
        }

        &::placeholder {
            color: $text-mid-emphasis;
        }

        &:disabled {
            background-color: $card-neutral-background;
            opacity: 0.6;
        }

        &:hover:not(:disabled) {
            border-color: $primary-accent;
        }

        &:active:not(:disabled) {
            border-color: $primary-accent;
        }

        &.-invalid {
            border-color: $border-error;
        }

        &.-side-sign {
            border-radius: 0px 10px 10px 0px;
        }

        &[type='number'] {
            -moz-appearance: textfield;
        }

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
    }

    &.-has-save input {
        padding-right: 50px;
    }

    &.-have-icon:not(.-only-search) input {
        padding-left: 34px;

        &.-sm {
            padding-left: 32px;
        }

        &.-lg {
            padding-left: 36px;
        }
    }

    &.-clear-button input {
        padding-right: 40px;

        &.-sm {
            padding-right: 32px;
        }

        &.-lg {
            padding-right: 36px;
        }
    }

    &__icon-wrapper {
        position: absolute;
        top: 0;
        height: 100%;
        width: 100%;
        padding: 0 12px;
        display: flex;
        align-items: center;
        justify-content: space-between;

        &.-only-search,
        &.-has-save,
        &.-have-unit-type,
        &.-password-type {
            justify-content: flex-end;
        }

        &.-with-label {
            top: 29px;
            height: 40px;

            &.-lg {
                height: 40px;
            }

            &.-sm {
                height: 32px;
            }
        }

        &.-sm {
            padding: 0 8px;
        }
    }

    &__error {
        @include fz-12;
        font-weight: 500;
        color: $border-error;
    }

    &__label {
        color: $text-high-emphasis;
        @include fz-14;
        font-weight: 500;

        &-wrapper {
            display: flex;
            align-items: center;
            gap: 8px;
        }
    }

    &__required {
        color: $primary-accent;
    }

    &__prompt {
        color: $text-high-emphasis;
        @include fz-12;
        margin-top: -4px;
    }
}
</style>
